import type { ActiveSupportAccessGrantsQuery } from "src/graphql";
import { formatDuration } from "src/utils/time";

// Docs: https://www.notion.so/hightouch/State-of-Impersonation-28eab9c473d58047bba1c80bb3b5e953?source=copy_link
export type SupportAccessType =
  | "write_grant_required"
  | "read_write_grant_required"
  | "none"
  | "full";

export type SupportAccessGrant =
  ActiveSupportAccessGrantsQuery["support_access_grant"][number];

/**
 * Returns the most distantly expiring support access grant that applies to the
 * workspaceId if provided, or the full organization if omitted. For a support
 * access grant to apply to a workspace, it must be either org-wide (null workspace_id,
 * or have a matching workspace id)
 */
export const getActiveSupportAccessGrant = (
  activeSupportAccessGrants: SupportAccessGrant[],
  workspaceId?: string,
): SupportAccessGrant | undefined => {
  return activeSupportAccessGrants
    .filter(
      (grant) => !grant.workspace_id || grant.workspace_id === workspaceId,
    )
    .sort(
      (a, b) =>
        new Date(b.expires_at).getTime() - new Date(a.expires_at).getTime(),
    )[0];
};

export const formatDurationUntilExpiration = (expiresAt: string) =>
  formatDuration(
    {
      start: new Date().toISOString(),
      end: expiresAt,
    },
    {
      units: "short",
      granularity: "minute",
    },
  );

export const supportAccessTypesRequiringControl: SupportAccessType[] = [
  "read_write_grant_required",
  "write_grant_required",
];

/**
 * Type describing the user's permission on the "Support access" tab of Workspace/Org settings
 * `readonly`: the page should be visible but read-only
 * `write`: user can enable/disable support access
 */
export enum UserSupportAccessControlPermission {
  Readonly = "readonly",
  Write = "write",
}
